import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Aggrid from "../../../framework/AgGrid";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import GlobalContext from "../../../framework/GlobalContext";
import { getCategory } from "../../../services/category-services";
import { getCustomerDetails } from "../../../services/customer-services";
import { fetchLoyaltyProgramDetails } from "../../../services/discount-program";
import { listEntity } from "../../../services/entity-services";
import { rowDown, rowUp, selectRow } from "../../../utils/gridUtils";
import classes from "./index.module.css";

const DiscountProgramDetails = (props) => {
  const { discountProgramCode = null, onEscape = () => {} } = props;
  const [discountData, setDiscountData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Product Category Id",
        field: "referenceId",
        width: 400,
      },
      {
        headerName: "Product Category",
        field: "productCategoryName",
        width: 475,
      },
      {
        headerName: "Discount",
        field: "discPer",
        width: 400,
      },
    ];
  }, []);

  const findCategoryInHierarchy = useCallback(
    (categories, categoryId, parent = null) => {
      for (const category of categories) {
        if (category.categoryId === categoryId || category.id === categoryId) {
          return {
            value: category,
            parent: parent ? { ...parent } : null,
          };
        }
        if (category.childs && category.childs.length > 0) {
          const found = findCategoryInHierarchy(category.childs, categoryId, {
            value: category,
            parent: parent ? { ...parent } : null,
          });
          if (found) {
            return found;
          }
        }
      }
      return null;
    },
    []
  );

  const getDiscountProgramDetails = useCallback(
    async (discountProgramCode) => {
      await fetchLoyaltyProgramDetails(`_id=${discountProgramCode}`).then(
        async (resp) => {
          const data = resp?.result?.rows;
          if (data?.referenceType === "patient") {
            const customer = await getCustomerDetails(
              `patientId=${data?.referenceId}`
            );
            setDiscountData({
              ...data,
              customer: customer?.result?.rows?.patientName,
            });
          }
          if (data?.referenceType === "entity") {
            const allEntity = await listEntity();
            const entity = allEntity?.result?.rows?.find(
              (e) => e.entityId === data?.referenceId
            );
            setDiscountData({
              ...data,
              customer: entity?.entityName,
            });
          }
          const updatedRowData = await Promise.all(
            data?.loyaltyProgramDetails?.map(async (item) => {
              const categories = await getCategory();
              const categoryData = await findCategoryInHierarchy(
                categories,
                item?.referenceId
              );
              return {
                ...item,
                productCategoryName: categoryData?.value?.categoryName,
              };
            })
          );
          setRowData(updatedRowData);
        }
      );
    },
    [findCategoryInHierarchy]
  );

  const onGridReadyHandler = useCallback(
    (params) => {
      setApi(params.api);
      params.api.sizeColumnsToFit();
      selectRow(params.api, selectedRowIndex);
    },
    [selectedRowIndex]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api);
        return;
      }
      if (key === "arrowup") {
        rowUp(api);
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (discountProgramCode) {
      getDiscountProgramDetails(discountProgramCode);
    }
  }, [getDiscountProgramDetails, discountProgramCode]);

  useEffect(() => {
    setCurrentComponent("grid_discount_program_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_discount_program_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["discount-program-details"]}>
      <div className={classes["discount-program-details-header"]}>
        <div className={classes["discount-program-details-header-left"]}>
          <span>
            {discountData?.programName?.toUpperCase()} -{" "}
            {discountData?.discountProgramCode}
          </span>
          <span>
            From -{" "}
            {moment(`${discountData?.startDate}`).format("DD/MM/YYYY hh:mm A")}
          </span>
          <span>
            To -{" "}
            {moment(`${discountData?.endDate}`).format("DD/MM/YYYY hh:mm A")}
          </span>
        </div>
        <div className={classes["discount-program-details-header-right"]}>
          {discountData?.referenceId && (
            <span>
              {discountData?.referenceType === "entity" ? "Entity" : "Customer"}{" "}
              - {discountData?.customer?.toUpperCase()}{" "}
            </span>
          )}
        </div>
      </div>

      <div className={classes["discount-program-details-grid"]}>
        <Aggrid
          id="grid_discount_program_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={(params) => {
            setTimeout(() => {
              onGridReadyHandler(params);
            }, 100);
          }}
          onCellClicked={(params) => {
            setSelectedRowIndex(params.rowIndex);
          }}
        />
      </div>
    </div>
  );
};

export default DiscountProgramDetails;
